<script>
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { REUSE_POTENTIAL_INDEX } from '@/modules/modules';
import AntInput from '@/components/AntInput.vue';
import { createRecord } from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';

export default {
  name: 'ReusePotentialIndex',
  components: { AntInput, ModuleNavigationBar },
  data: () => {
    return {
      variants: [],
      variantsTableId: null,
      newVariantMenu: false,
      newVariantName: '',
      newVariantContact: '',
      variantLoading: false,
      focusedVariantId: '',
      focusedCrossSectionId: '',
      navigation: 'overview',
    };
  },
  computed: {
    ...mapGetters(['project']),
  },
  watch: {
    navigation(value) {
      switch (value) {
        case 'overview':
          this.$router.push({
            name: `${REUSE_POTENTIAL_INDEX}`,
          });
          this.focusedVariantId = '';
          this.fetchVariants();
          break;

        case 'cross-sections':
          this.$router.push({
            name: `${REUSE_POTENTIAL_INDEX}-cross-sections`,
            params: { variant: this.focusedVariantId },
          });
          break;

        case 'calculation':
          this.$router.push({
            name: `${REUSE_POTENTIAL_INDEX}-calculation`,
            params: {
              variant: this.focusedVariantId,
              crossSection: this.focusedCrossSectionId,
            },
          });
          break;

        case 'results':
          this.$router.push({
            name: `${REUSE_POTENTIAL_INDEX}-results`,
            params: {
              variant: this.focusedVariantId,
            },
          });
          break;
      }
    },
    $route: {
      immediate: true,
      handler(value) {
        this.setNavigation();
        this.setFocusedVariant();
        this.setFocusedCrossSection();
      },
    },
  },
  mounted() {
    this.setNavigation();
    this.fetchVariants();
    this.setFocusedVariant();
    this.setFocusedCrossSection();
  },
  methods: {
    setNavigation() {
      switch (this.$route.name) {
        case REUSE_POTENTIAL_INDEX:
          this.navigation = 'overview';
          break;
        case `${REUSE_POTENTIAL_INDEX}-cross-sections`:
          this.navigation = 'cross-sections';
          break;
        case `${REUSE_POTENTIAL_INDEX}-calculation`:
          this.navigation = 'calculation';
          break;
        case `${REUSE_POTENTIAL_INDEX}-results`:
          this.navigation = 'results';
          break;
      }
    },
    setFocusedVariant() {
      if (this.$route.params.variant) {
        this.focusedVariantId = this.$route.params.variant;
      }
    },
    setFocusedCrossSection() {
      if (this.$route.params.crossSection) {
        this.focusedCrossSectionId = this.$route.params.crossSection;
      }
    },
    async fetchVariants() {
      this.variantLoading = true;
      try {
        let { variants } = await queryTablesV2({
          tables: [
            {
              name: 'CFFA_RPI_VARIANTS',
              project: this.project.id,
              as: 'variants',
              columns: [
                {
                  name: 'name',
                },
                {
                  name: 'contact',
                },
                {
                  name: 'date',
                },
              ],
            },
          ],
        });
        this.variants = variants.records;
        this.variantsTableId = variants.id;
      } catch (e) {}
      this.variantLoading = false;
    },
    async createVariant() {
      this.variantLoading = true;
      try {
        let variant = await createRecord({
          project: { id: this.project.id },
          table: { id: this.variantsTableId },
          record: {
            name: this.newVariantName,
            contact: this.newVariantContact,
            date: moment().format('YYYY-MM-DD'),
          },
        });
        this.variants.push(variant);
        this.newVariantMenu = false;
        this.newVariantName = '';
        this.newVariantContact = '';
      } catch (e) {}
      this.variantLoading = false;
    },
    focusOnVariant(variant) {
      this.focusedVariantId = variant.id;
      this.navigation = 'cross-sections';
    },
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD');
    },
  },
};
</script>

<template>
  <div class="d-flex flex-column overflow-y-auto">
    <module-navigation-bar title="">
      <template #module-nav-header>
        Reuse Potential Index (RP
        <div class="d-flex align-end fs-10" style="height: 30px">E.D.</div>
        )
      </template>
      <template #module-nav-actions>
        <v-btn-toggle v-model="navigation" color="primary" group tile>
          <v-btn value="overview"> Overview</v-btn>

          <v-btn v-if="focusedVariantId" value="cross-sections">
            Cross-sections
          </v-btn>

          <v-btn
            v-if="focusedVariantId && focusedCrossSectionId"
            value="calculation"
          >
            Calculation
          </v-btn>

          <v-btn v-if="focusedVariantId" value="results"> Results</v-btn>
        </v-btn-toggle>
      </template>
    </module-navigation-bar>
    <div
      class="d-flex flex-column overflow-auto flex-grow-1"
      style="width: 100vw"
    >
      <span class="fs-12 my-1 mx-5">@ Method E. Durmisevic</span>
      <div class="flex-grow-1 d-flex overflow-auto" style="height: 0">
        <router-view v-if="focusedVariantId" class="flex-grow-1 d-flex" />
        <div v-else class="d-flex flex-column px-5 flex-grow-1">
          <div class="mb-5 pa-5 background-white full-width">
            <v-subheader class="subheader">Explanation</v-subheader>
            <p>
              To calculate the Reuse Potential Index (RPI), you need to go
              through the following steps.<br /><br />

              1. Create a variant. For each design, several variants can be
              calculated. Here you have an overview of the different variants
              and you can create a variant.<br /><br />

              2. For each variant you can create a reference cross-section.
              This is the build-up of a facade that you are going to calculate.
              Each cross-section has a certain coverage ratio (the ratio of
              the facade build-up to the total facade). This can be expressed
              in percentages, meters or other units (as long as you keep it
              consistent).<br /><br />

              3. For each reference cross-section you can add the parts.
              You can then indicate per part which properties it has and
              which other parts it is connected to.<br /><br />

              4. Next you can go to the results tab to view the RPI, compare
              it to other reference cross-sections relative to the total. In
              addition, there are tips available to increase reusability.<br /><br />
            </p>
          </div>
          <v-data-table
            :headers="[
              { text: 'Name', value: 'name' },
              { text: 'Contact person', value: 'contact' },
              { text: 'Created on', value: 'date' },
            ]"
            :items="variants"
            :loading="variantLoading"
            sort-by="date"
            sort-desc
            @click:row="focusOnVariant"
          >
            <template #item.date="{ value }">
              {{ formatDate(value) }}
            </template>
            <template #top>
              <v-toolbar flat>
                <v-toolbar-title>Variants</v-toolbar-title>
                <v-spacer />
                <v-menu
                  v-model="newVariantMenu"
                  :close-on-content-click="false"
                  left
                >
                  <template #activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-text>
                      <ant-input label="Name">
                        <template #input-field>
                          <v-text-field
                            v-model="newVariantName"
                            autofocus
                            class="mb-2"
                            dense
                            filled
                            hide-details
                            placeholder="Name"
                            single-line
                          />
                        </template>
                      </ant-input>
                      <ant-input label="Contact person">
                        <template #input-field>
                          <v-text-field
                            v-model="newVariantContact"
                            class="mb-2"
                            dense
                            filled
                            hide-details
                            placeholder="Contact person"
                            single-line
                          />
                        </template>
                      </ant-input>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn color="primary" small @click="createVariant"
                        >Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-toolbar>
            </template>
          </v-data-table>
        </div>
      </div>
      <div class="fs-12 my-1 mx-5">
        <span class="font-weight-bold">©4D Architects Amsterdam</span>
        (RP<span style="font-size: 6px">E.D.</span>) Method Elma Durmisevic
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.subheader {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: var(--v-primary-base);
  font-size: 20px;
}
</style>
